import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { notifySuccess } from "@/lib/notify";

import { buildStarterModel } from "./build-starter-model";
import { modelFileBody } from "./model-file-body";
import {
  deleteProcessModel,
  useInvalidateDesignLists,
} from "./use-delete-model";

/**
 * モデルの中身をどこから作るか。
 *
 * `starter` は雛形（起案タスク 1 つ＋起案フォーム）。`file` は手持ちの .bpmn を
 * そのまま置く。file の場合は起案フォームを作らない — 外部の BPMN に、フォームを
 * 紐づけられる User Task があるとは限らないため。
 *
 * file でも保存名は `<モデル名>.bpmn` に固定する（選んだファイルの名前は使わない）。
 */
export type CreateModelSource =
  | { readonly kind: "starter" }
  | {
      readonly kind: "file";
      readonly xml: string;
      /** `readBpmnMeta` が XML から読んだ実行可能プロセスの id。 */
      readonly processId: string;
    };

export type CreateModelInput = {
  /** 追加先のプロセスグループ id（入れ子可）。 */
  readonly group: string;
  /** モデル名（ディレクトリ名になる）。 */
  readonly modelName: string;
  readonly displayName: string;
  readonly description?: string;
  /** 省略時は雛形から作る。 */
  readonly source?: CreateModelSource;
  /**
   * 追加先グループ（`group`）を同時に作る場合の表示名。既存グループを選んだときは
   * undefined。id は `group` を使う（別に持つと食い違いうる）。
   */
  readonly newGroup?: {
    readonly displayName: string;
  };
};

/**
 * モデルを新規作成する。
 *
 * 最大 4 手に分かれる（backend の API がこの形）。
 *   0. グループを作る（新しいグループを指定したときだけ）
 *   1. グループ配下にモデルを作る。backend はこのとき `<モデル名>.bpmn` の雛形
 *      （乱数付きのプロセス id）を必ず書く
 *   2. BPMN を `<モデル名>.bpmn` で追加する（1 の雛形を上書きする）
 *   3. 起案フォームの JSON Schema を追加する（雛形のときだけ）
 *
 * 0 はモデルより**先**に行う。backend の `process_model_create` は先頭でグループの
 * 存在を見ており、無いと 500（本文に `process_group_not_found`）になるため。
 *
 * 件名の予約キー（`case_title`）の `metadata_extraction_paths` は 1 の時点で渡す。
 * 後回しにすると宣言漏れのまま運用され、一覧に件名が出ない・検索できないモデルが
 * できてしまう。持ち込みの BPMN でも宣言だけは入れる（値が入るのは、図の中で
 * `case_title` という変数に値を入れたとき。入らない間は件名が空欄になる）。
 */
export async function createProcessModel(
  client: ApiClient,
  input: CreateModelInput,
): Promise<{ readonly processModelId: string }> {
  const processModelId = `${input.group}/${input.modelName}`;
  const starter = buildStarterModel({
    processModelId,
    displayName: input.displayName,
    ...(input.description === undefined
      ? {}
      : { description: input.description }),
  });

  let createdGroup: CreateModelError["createdGroup"] = null;
  if (input.newGroup !== undefined) {
    try {
      await client.post("/process-groups", {
        id: input.group,
        display_name: input.newGroup.displayName,
        description: "",
      });
    } catch (cause) {
      throw new CreateModelError("group", null, null, cause);
    }
    createdGroup = { id: input.group, displayName: input.newGroup.displayName };
  }

  const source: CreateModelSource = input.source ?? { kind: "starter" };
  const config =
    source.kind === "starter"
      ? starter.config
      : { ...starter.config, primary_process_id: source.processId };

  try {
    await client.post(
      `/process-models/${toModifiedProcessModelId(input.group)}`,
      config,
    );
  } catch (cause) {
    throw new CreateModelError("model", createdGroup, null, cause);
  }

  const filesPath = `/process-models/${toModifiedProcessModelId(processModelId)}/files`;

  try {
    if (source.kind === "starter") {
      await client.post(
        filesPath,
        modelFileBody(starter.bpmnFileName, starter.bpmnXml),
      );
      await client.post(
        filesPath,
        modelFileBody(starter.formFileName, starter.formJson),
      );
    } else {
      await client.post(
        filesPath,
        modelFileBody(starter.bpmnFileName, source.xml),
      );
    }
  } catch (cause) {
    // 中身（BPMN・起案フォーム）が欠けたモデルは起票できないので残さない
    const leftover = await deleteProcessModel(client, processModelId).then(
      () => null,
      () => processModelId,
    );
    throw new CreateModelError("files", createdGroup, leftover, cause);
  }

  return { processModelId };
}

/**
 * 作成が途中で失敗したときの状況。
 *
 * API を順に呼ぶので、前の手順で作ったものが残る。画面はこれを見て、
 * 再試行が必ず失敗する状態（同じ id が既にある）にならないよう入力を切り替える。
 * グループは削除しない。空でも害が小さく、再試行でそのまま使えるため。
 */
export class CreateModelError extends Error {
  /** どの手順で失敗したか。 */
  readonly stage: "group" | "model" | "files";
  /** この操作で作れたグループ（新しく作る指定で、作成に成功した場合だけ）。 */
  readonly createdGroup: {
    readonly id: string;
    readonly displayName: string;
  } | null;
  /** 削除して戻せなかった作りかけのモデル。 */
  readonly leftoverModelId: string | null;
  override readonly cause: unknown;

  constructor(
    stage: CreateModelError["stage"],
    createdGroup: CreateModelError["createdGroup"],
    leftoverModelId: string | null,
    cause: unknown,
  ) {
    super(`process model creation failed at ${stage}`);
    this.name = "CreateModelError";
    this.stage = stage;
    this.createdGroup = createdGroup;
    this.leftoverModelId = leftoverModelId;
    this.cause = cause;
  }
}

export function useCreateModel(): UseMutationResult<
  { readonly processModelId: string },
  Error,
  CreateModelInput
> {
  const client = useApiClient();
  const invalidate = useInvalidateDesignLists();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: CreateModelInput) => createProcessModel(client, input),
    onSuccess: (_result, input) => {
      notifySuccess(t("toast.modelCreated", { name: input.displayName }));
    },
    // 途中で失敗しても、作れたグループや削除できなかったモデルが一覧に増える
    onSettled: invalidate,
  });
}
